import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Dumbbell, CheckCircle2, Salad, ChevronRight, Trophy } from 'lucide-react';
import { api, getStoredUser } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

export default function Home() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [user, setUser] = useState(getStoredUser());
  const [checkins, setCheckins] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    const u = getStoredUser();
    if (!u?.id) return;
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
      localStorage.setItem('user', JSON.stringify(me.data));
      const c = await api.get(`/checkins/${u.id}`);
      setCheckins(c.data || []);
      const n = await api.get('/notifications');
      setUnread((n.data || []).filter((x) => !x.read).length);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const sw = user?.start_weight, cw = user?.current_weight, tw = user?.target_weight;
  const goalGain = !!(sw && tw && tw > sw);
  const goalLose = !!(sw && tw && tw < sw);
  const delta = sw && cw ? Math.round((cw - sw) * 10) / 10 : 0;
  const absDelta = Math.abs(delta);
  const pct = sw && cw && tw && sw !== tw ? Math.max(0, Math.min(100, Math.round(((cw - sw) / (tw - sw)) * 100))) : 0;
  const goalReached = !!(sw && cw && tw && ((goalLose && cw <= tw) || (goalGain && cw >= tw)));
  const progressLabel = goalGain ? t('home.gained') : goalLose ? t('home.lost') : t('home.change');
  const progressPrefix = goalGain ? '+' : goalLose ? '−' : delta > 0 ? '+' : delta < 0 ? '−' : '';

  const lastCi = checkins[0];
  const daysSince = lastCi ? Math.floor((Date.now() - new Date(lastCi.created_at).getTime()) / 86400000) : 999;
  const checkinDue = daysSince >= 7;

  const actions = [
    { icon: Dumbbell, title: t('home.startTraining'), sub: t('home.todaySplit'), to: '/app/training', tid: 'quick-training' },
    { icon: CheckCircle2, title: t('home.checkin'), sub: checkinDue ? t('home.checkinDue') : `${daysSince} ${t('home.daysAgo')}`, to: '/checkin', tid: 'quick-checkin', badge: checkinDue },
    { icon: Salad, title: t('home.nutrition'), sub: t('home.nutritionSub'), to: '/app/nutrition', tid: 'quick-nutrition' },
  ];

  return (
    <div className="px-6 pt-10 animate-fade-in" data-testid="client-home">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="as-label">{t('home.welcome')}</p>
          <h1 className="mt-1.5 font-display text-4xl font-extrabold tracking-tight text-white">{user?.first_name || ''}</h1>
        </div>
        <div className="flex items-center gap-2.5">
          <button data-testid="notifications-btn" onClick={() => nav('/notifications')} className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/[0.06] bg-asgym-surface text-white">
            <Bell size={18} />
            {unread > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-asgym-accent px-1 text-[9px] font-black text-white">{unread > 9 ? '9+' : unread}</span>}
          </button>
          <span className="as-chip border-asgym-accent/40 bg-asgym-accent/10 text-asgym-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-asgym-accent" /> {t('home.active')}
          </span>
        </div>
      </div>

      <div className="rounded-3xl border border-white/[0.06] p-6 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.8)]" style={{ background: 'linear-gradient(135deg, rgba(28,29,33,0.95), rgba(16,17,20,0.95))' }} data-testid="status-card">
        <p className="as-label">{t('home.progress')}</p>
        <div className="mt-2 flex items-baseline">
          <span className="font-display text-6xl font-extrabold tracking-tighter text-white">{cw ? `${cw}` : '—'}</span>
          <span className="ml-1.5 text-lg font-semibold text-asgym-sub">kg</span>
        </div>
        <p className="mb-4 text-[13px] text-asgym-sub">{progressPrefix}{absDelta} {progressLabel} · {t('home.target')} {tw || '—'}kg</p>
        {goalReached && (
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-asgym-accent px-2.5 py-1 text-[11px] font-black text-black"><Trophy size={13} /> {t('home.goalReached')}</div>
        )}
        <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#00C896,#00B383)' }} />
        </div>
        <div className="mt-2.5 flex justify-between text-[11px] font-semibold text-asgym-muted">
          <span>{sw || '—'}kg</span><span className="font-bold text-white">{pct}%</span><span>{tw || '—'}kg</span>
        </div>
      </div>

      <p className="as-section mt-8 mb-4">{t('home.today')}</p>
      <div className="space-y-2.5">
        {actions.map((a) => (
          <button key={a.tid} data-testid={a.tid} onClick={() => nav(a.to)} className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors ${a.badge ? 'border-asgym-accent/40 bg-asgym-accent/10' : 'border-white/[0.06] bg-asgym-surface hover:bg-asgym-elevated'}`}>
            <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${a.badge ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.06] bg-white/[0.03] text-white'}`}>
              <a.icon size={20} />
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-bold text-white">{a.title}</p>
              <p className={`text-xs ${a.badge ? 'font-bold text-asgym-accent' : 'text-asgym-muted'}`}>{a.sub}</p>
            </div>
            {a.badge ? <span className="h-2 w-2 rounded-full bg-asgym-accent" /> : <ChevronRight size={20} className="text-asgym-muted" />}
          </button>
        ))}
      </div>

      <p className="as-section mt-8 mb-4">{t('home.lastCheckin')}</p>
      <div className="as-card p-4">
        {lastCi ? (
          <>
            <p className="mb-4 text-[13px] font-bold text-white">{new Date(lastCi.created_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            <div className="flex gap-4">
              <Mini label={t('home.weight')} val={lastCi.weight ? `${lastCi.weight} kg` : '—'} />
              <Mini label={t('home.motivation')} val={`${lastCi.motivation || '—'}/10`} />
              <Mini label={t('home.sleep')} val={`${lastCi.sleep_quality || '—'}/10`} />
            </div>
          </>
        ) : (
          <p className="text-[13px] leading-relaxed text-asgym-sub">{t('home.empty')}</p>
        )}
      </div>
    </div>
  );
}

const Mini = ({ label, val }) => (
  <div className="flex-1">
    <p className="text-[9px] font-bold tracking-[1.5px] text-asgym-muted">{label.toUpperCase()}</p>
    <p className="mt-1 text-base font-bold text-white">{val}</p>
  </div>
);
